import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, PROFILE } from "@/data/site";
import { HMark } from "@/components/Monogram";
import { lockScroll, scrollToTarget } from "@/lib/scroll";
import { EASE } from "@/lib/motion";

const Available = ({ testId }) => (
  <span className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-ash" data-testid={testId}>
    <span className="relative flex h-2 w-2">
      <span className="pulse-ring absolute inset-0 rounded-full bg-gold" />
      <span className="relative h-2 w-2 rounded-full bg-gold" />
    </span>
    Available for Projects
  </span>
);

const MobileMenu = ({ onGo }) => (
  <motion.div
    className="fixed inset-0 z-40 flex flex-col justify-between bg-ink px-6 pb-10 pt-28 md:hidden"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE }}
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col" aria-label="Mobile">
      {NAV_LINKS.map((l, i) => (
        <motion.button
          key={l.id}
          onClick={() => onGo(l.target)}
          className="flex items-baseline justify-between border-b border-line py-5 text-left font-serif text-5xl font-light text-ivory"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 + i * 0.07, ease: EASE }}
          data-testid={`mobile-nav-link-${l.id}`}
        >
          {l.label}
          <span className="font-mono text-xs text-gold">0{i + 1}</span>
        </motion.button>
      ))}
    </nav>
    {PROFILE.available && <Available testId="mobile-available-status" />}
  </motion.div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    lockScroll(open);
    return () => lockScroll(false);
  }, [open]);

  const go = (target) => {
    setOpen(false);
    if (pathname === "/") setTimeout(() => scrollToTarget(target), open ? 350 : 0);
    else navigate(target === "#top" ? "/" : `/${target}`);
  };

  const solid = scrolled || open;
  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        solid ? "border-b border-line bg-ink/80 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-20 items-center justify-between">
        <Link to="/" onClick={() => setOpen(false)} className="group flex items-center gap-3" data-testid="nav-wordmark" aria-label="HADES — home">
          <HMark className="h-7 w-7 transition-transform duration-700 group-hover:rotate-[360deg]" />
          <span className="wordmark text-lg text-ivory">Hades</span>
        </Link>
        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.target)}
              className="link-line font-mono text-xs uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-ivory"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden lg:block">{PROFILE.available && <Available testId="nav-available-status" />}</div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="relative z-50 flex h-12 w-12 flex-col items-center justify-center gap-[7px] md:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          <span className={`h-px w-7 bg-ivory transition-transform duration-500 ${open ? "translate-y-[4px] rotate-45" : ""}`} />
          <span className={`h-px w-7 bg-ivory transition-transform duration-500 ${open ? "-translate-y-[4px] -rotate-45" : ""}`} />
        </button>
      </div>
    </header>
    <AnimatePresence>{open && <MobileMenu onGo={go} />}</AnimatePresence>
    </>
  );
};
